import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Custom hook to enforce buy/sell trading limits before a trade is executed.
 *
 * Rules checked:
 *  1. limit_exceeded flag is true → trade is blocked entirely
 *  2. deal_{action}_limit_{currency} >= notionalValue  (per-deal notional cap)
 *  3. {action}_limit_{currency} >= {action}_total_{currency} + notionalValue  (cumulative notional cap)
 *  4. net_buy_sell_limit_{currency} >= net_buy_sell_total_{currency} + notionalValue  (net notional cap)
 *  5. {action}_limit_mw >= {action}_total_mw + volume  (cumulative MW cap)
 *
 * @param {object} params
 * @param {'buy'|'sell'} params.action  - Trade direction
 * @param {'eur'|'chf'}  params.currency - Currency code (case-insensitive)
 * @param {number|string} params.notionalValue - Pre-computed notional value of this trade
 * @param {number|string} params.volume - Volume in MW entered by the user
 *
 * @returns {{ isDisabled: boolean, messages: string[], isLoading: boolean, limits: object|null }}
 */
const useLimitCheck = ({ action, currency, notionalValue, volume }) => {
    const [limits, setLimits] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const curr = currency.toLowerCase();

    useEffect(() => {
        const fetchLimits = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`/api/limits/${action}/${curr}`);
                setLimits(response.data);
            } catch (err) {
                console.error(`Failed to fetch limits for ${action}/${curr}:`, err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchLimits();
    }, [action, curr]);

    const violations = [];

    if (limits) {
        const notional = parseFloat(notionalValue) || 0;
        const vol = parseFloat(volume) || 0;
        const currUpper = curr.toUpperCase();
        const actionLabel = action === 'buy' ? 'Buy' : 'Sell';

        // Rule 1: Overall limit exceeded flag
        if (limits.limit_exceeded) {
            violations.push('Daily Buy or Sell Limit has already been exceeded. Please contact your risk manager to change your limits.');
        } else {
            // Rule 2: Per-deal notional cap  (deal_{action}_limit_{curr} >= notionalValue)
            const dealLimitKey = `deal_${action}_limit_${curr}`;
            const dealLimit = limits[dealLimitKey];
            if (dealLimit !== undefined && notional > 0 && notional > dealLimit) {
                violations.push(
                    `Order value of this trade (${notional.toFixed(3)} ${currUpper}) exceeds the per-trade limit of ${dealLimit} ${currUpper}. Please contact your risk manager to change your limits.`
                );
            }
            // Rule 2(mw): Per-deal MW cap  (deal_{action}_limit_mw >= volume)
            const dealLimitMwKey = `deal_${action}_limit_mw`;
            const dealLimitMw = limits[dealLimitMwKey];
            if (dealLimitMw !== undefined && vol > 0 && vol > dealLimitMw) {
                violations.push(
                    `Order volume of this trade (${vol.toFixed(3)} MW) exceeds the max per-deal MW limit of ${dealLimitMw} MW. Please contact your risk manager to change your limits.`
                );
            }

            // Rule 3: Cumulative currency notional cap  ({action}_limit_{curr} >= {action}_total_{curr} + notionalValue)
            const actionLimitKey = `${action}_limit_${curr}`;
            const actionTotalKey = `${action}_total_${curr}`;
            const actionLimit = limits[actionLimitKey];
            const actionTotal = limits[actionTotalKey] ?? 0;
            if (actionLimit !== undefined && notional > 0 && actionTotal + notional > actionLimit) {
                violations.push(
                    `Daily allowed cumulative value of trades after this trade (${(actionTotal + notional).toFixed(3)} ${currUpper}) would exceed the ${actionLabel.toLowerCase()} limit allowed of ${actionLimit} ${currUpper}. (Current total: ${actionTotal} ${currUpper}) Please contact your risk manager to change your limits.`
                );
            }

            // Rule 4: Net buy/sell cumulative notional cap  (net_buy_sell_limit_{curr} >= net_buy_sell_total_{curr} + notionalValue)
            // const netLimitKey = `net_buy_sell_limit_${curr}`;
            // const netTotalKey = `net_buy_sell_total_${curr}`;
            // const netLimit = limits[netLimitKey];
            // const netTotal = limits[netTotalKey] ?? 0;
            // if (netLimit !== undefined && notional > 0 && netTotal + notional > netLimit) {
            //     violations.push(
            //         `Daily Net buy/sell cumulative value after this trade (${(netTotal + notional).toFixed(3)} ${currUpper}) would exceed the net limit of ${netLimit} ${currUpper}. (Current net total: ${netTotal} ${currUpper}) Please contact your risk manager to change your limits.`
            //     );
            // }

            // Rule 5: Cumulative MW cap  ({action}_limit_mw >= {action}_total_mw + volume)
            const mwLimitKey = `${action}_limit_mw`;
            const mwTotalKey = `${action}_total_mw`;
            const mwLimit = limits[mwLimitKey];
            const mwTotal = limits[mwTotalKey] ?? 0;
            if (mwLimit !== undefined && vol > 0 && mwTotal + vol > mwLimit) {
                violations.push(
                    `Daily cumulative volume after this trade (${(mwTotal + vol).toFixed(3)} MW) would exceed the ${actionLabel.toLowerCase()} MW limit of ${mwLimit} MW. (Current total: ${mwTotal} MW) Please contact your risk manager to change your limits.`
                );
            }
        }
    }

    return {
        isDisabled: violations.length > 0,
        messages: violations,
        isLoading,
        limits
    };
};

export default useLimitCheck;
